import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LayoutService } from '../layout/layout.service';
import { menuItems as servicenearMenuItems, moreMenuItems as servicenearMoreMenuItems } from '../third-party/service-near/menu';
import { sideMenuItems, profileMenuItems } from 'src/assets/tobeonlineMenu';
import { SubscriptionsService } from './subscriptions.service';

/** A routable entry of the static menus, with the ids of its parents (outermost first). */
interface FeatureRouteNode {
  id: string;
  path: string;
  parentIds: string[];
}

/** Menu paths that are actions or placeholders, never real pages. */
const NON_ROUTE_PATHS = ['', '/', '/admin/', '/?activateoldui=true', 'logout', '/logout'];

/** Pages that stay reachable whatever the plan says (e.g. paying a pending bill). */
const ALWAYS_ALLOWED_PATHS = ['/profile/SubscriptionDetails'];

/**
 * Subscription feature access for routes.
 * The active plan's feature JSON has a "mobilemenu" tree of { id, show, disable, positionindex, children }.
 * A route is mapped to a menu id (route data.featureId, or the static menu entry whose path matches the url)
 * and is blocked when that id - or any of its parents - has show = false or disable = true.
 * Ids that are not configured in the plan are allowed.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureAccessService {

  private readonly platformMenuKey = 'mobilemenu';
  private routeNodesCache: { tobeonline?: FeatureRouteNode[], servicenear?: FeatureRouteNode[] } = {};

  constructor(
    private subscriptionsService: SubscriptionsService,
    private layoutService: LayoutService,
  ) { }

  /** Loads the plan features (cache first, API on first load / expiry). */
  public loadFeatures(): Observable<any> {
    return this.subscriptionsService.loadSubscriptionFeatures();
  }

  public isLoggedIn(): boolean {
    return !!this.getLoginDetails()?.UserID;
  }

  /** Superadmins manage other sellers, plan rules do not apply to them. */
  public isAccessCheckExempt(): boolean {
    return this.getLoginDetails()?.UserType === 'superadmin';
  }

  /** True when the url (or the given feature id) is allowed by the active subscription plan. */
  public canAccessUrl(url: string, featureId?: string): boolean {
    const path = this.cleanUrl(url);
    if (this.isAlwaysAllowed(path)) {
      return true;
    }
    if (featureId) {
      return this.isFeatureEnabled(featureId);
    }
    const node = this.findRouteNode(path);
    return node ? this.isFeatureEnabled(node.id, node.parentIds) : true;
  }

  /**
   * Subscription rule for a feature id: blocked when it, or any parent, is hidden (show = false) or disabled.
   * parentIds narrows the lookup when the same id exists in more than one place.
   */
  public isFeatureEnabled(featureId: string, parentIds: string[] = []): boolean {
    const menuConfig = this.getPlatformMenuConfig();
    if (!menuConfig?.length || !featureId) {
      return true; // no feature JSON for this plan - nothing to restrict
    }
    const configPath = this.findConfigPath(menuConfig, [...parentIds, featureId])
      ?? this.findConfigPath(menuConfig, [featureId]);
    if (!configPath) {
      return true;
    }
    return configPath.every((config: any) => config?.show !== false && config?.disable !== true);
  }

  /** The user's dashboard when allowed, otherwise the first enabled page in the plan's menu order. */
  public getFirstAccessibleRoute(): string | null {
    const isServicenear = this.isServicenearUser();
    const dashboards = isServicenear
      ? ['/servicenear/dashboard', '/dashboard/summary']
      : ['/dashboard/summary', '/servicenear/dashboard'];
    const dashboard = dashboards.find(path => this.canAccessUrl(path));
    if (dashboard) {
      return dashboard;
    }

    const nodes = this.getRouteNodes();
    const sortedConfig = this.flattenEnabledConfig(this.getPlatformMenuConfig() ?? []);
    for (const config of sortedConfig) {
      const path = nodes.find(n => n.id === config.id)?.path ?? config.path;
      if (path && !NON_ROUTE_PATHS.includes(path) && this.canAccessUrl(path)) {
        return path;
      }
    }
    // Plan has no order for the remaining pages - fall back to the static menu order
    return nodes.find(n => this.canAccessUrl(n.path))?.path ?? null;
  }

  public isServicenearUser(): boolean {
    return this.layoutService.getUserType() === 'servicenear'
      || this.getLoginDetails()?.UserType === 'servicenear'
      || /[?&]thirdpartyuserkey=/i.test(window.location.href);
  }

  // ---------------- Helpers ----------------

  private getPlatformMenuConfig(): any[] | null {
    const features = this.subscriptionsService.getSubscriptionFeatures();
    const menu = features?.[this.platformMenuKey];
    return Array.isArray(menu) ? menu : null;
  }

  /**
   * Finds the config nodes for an id chain. The first id is searched anywhere in the tree
   * (more-menu sections can be top level or nested under 'more'), the rest under its children.
   * Returns the config ancestors + matched nodes, or null when the chain is not configured.
   */
  private findConfigPath(menuConfig: any[], ids: string[]): any[] | null {
    const start = this.findConfigWithAncestors(menuConfig, ids[0]);
    if (!start) {
      return null;
    }
    const path = [...start];
    let level: any[] = start[start.length - 1]?.children;
    for (const id of ids.slice(1)) {
      const config = (level ?? []).find((item: any) => item?.id === id);
      if (!config) {
        return null;
      }
      path.push(config);
      level = config.children;
    }
    return path;
  }

  private findConfigWithAncestors(items: any[], id: string, ancestors: any[] = []): any[] | null {
    for (const item of items ?? []) {
      if (item?.id === id) {
        return [...ancestors, item];
      }
      if (item?.children?.length) {
        const found = this.findConfigWithAncestors(item.children, id, [...ancestors, item]);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  /** Enabled config entries in plan order (positionindex), parents before their children. */
  private flattenEnabledConfig(items: any[]): any[] {
    const sorted = [...(items ?? [])].sort((a: any, b: any) =>
      (a?.positionindex ?? Number.MAX_SAFE_INTEGER) - (b?.positionindex ?? Number.MAX_SAFE_INTEGER));
    const result: any[] = [];
    for (const item of sorted) {
      if (item?.show === false || item?.disable === true || item?.id === 'switcholdui') {
        continue;
      }
      result.push(item);
      result.push(...this.flattenEnabledConfig(item.children));
    }
    return result;
  }

  /** Static menu entry for a url: exact path match first, then the longest matching path prefix. */
  private findRouteNode(path: string): FeatureRouteNode | null {
    const nodes = this.getRouteNodes();
    const exact = nodes.find(node => node.path === path);
    if (exact) {
      return exact;
    }
    return nodes
      .filter(node => path.startsWith(node.path + '/'))
      .sort((a, b) => b.path.length - a.path.length)[0] ?? null;
  }

  private getRouteNodes(): FeatureRouteNode[] {
    if (this.isServicenearUser()) {
      return this.routeNodesCache.servicenear ??= [
        ...this.collectRouteNodes(servicenearMenuItems, 'children'),
        ...this.collectRouteNodes(servicenearMoreMenuItems, 'items'),
      ];
    }
    return this.routeNodesCache.tobeonline ??= [
      ...this.collectRouteNodes(sideMenuItems, 'children'),
      ...this.collectRouteNodes(profileMenuItems, 'children'),
    ];
  }

  private collectRouteNodes(items: any[], childKey: 'children' | 'items', parentIds: string[] = []): FeatureRouteNode[] {
    const nodes: FeatureRouteNode[] = [];
    for (const item of items ?? []) {
      if (!item?.id) {
        continue;
      }
      const path = this.cleanUrl(item.path ?? '');
      if (item.path && !NON_ROUTE_PATHS.includes(item.path) && !NON_ROUTE_PATHS.includes(path)) {
        nodes.push({ id: item.id, path, parentIds });
      }
      const children = item[childKey] ?? item.children;
      if (Array.isArray(children) && children.length) {
        nodes.push(...this.collectRouteNodes(children, childKey, [...parentIds, item.id]));
      }
    }
    return nodes;
  }

  private isAlwaysAllowed(path: string): boolean {
    return ALWAYS_ALLOWED_PATHS.some(allowed => path === allowed || path.startsWith(allowed + '/'));
  }

  /** Strips query string / fragment and the trailing slash so urls compare with menu paths. */
  private cleanUrl(url: string): string {
    const path = (url ?? '').split(/[?#]/)[0];
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }

  private getLoginDetails(): any {
    try {
      return JSON.parse(localStorage.getItem('admin_Details') ?? 'null');
    } catch (e) {
      return null;
    }
  }
}
